import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDataContext } from "../../contexts/AuthContext";
import Designer from "./Designer";
import styles from "./Designer.module.css";
import designerLoadingGif from "../../assets/designer/loading.gif";
import {
    getDesignerVariants,
    getIfGarmentIsReversible,
    getMasterTreeByOrderItemId,
} from "../../services/api";

const DESIGNER_BOOT_DELAY_MS = 2200;

const DesignerPage = () => {
    const navigate = useNavigate();
    const { orderItemId } = useParams();
    const { contextData } = useDataContext();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [masterTree, setMasterTree] = useState(null);
    const [bSide, setBSide] = useState(0);
    const [variants, setVariants] = useState([]);
    const [selectedProductModelId, setSelectedProductModelId] = useState(null);
    const [switchingVariant, setSwitchingVariant] = useState(false);
    const [variantError, setVariantError] = useState("");

    useEffect(() => {
        let cancelled = false;
        const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
        const run = async () => {
            if (!orderItemId) {
                setError("Falta order_item_id.");
                setLoading(false);
                return;
            }
            setLoading(true);
            setError("");
            setVariantError("");
            try {
                const [variantsResponse] = await Promise.all([
                    getDesignerVariants(orderItemId),
                    sleep(DESIGNER_BOOT_DELAY_MS),
                ]);
                const variantList = variantsResponse?.variants || [];
                const initialVariant =
                    variantList.find((item) => item?.selected)
                    || variantList[0]
                    || null;
                const initialProductModelId = initialVariant?.product_model_id || null;
                const reversible = await getIfGarmentIsReversible(orderItemId, initialProductModelId);
                const b = Number(reversible?.is_reversible) === 1 ? 1 : 0;
                if (!cancelled) setBSide(b);
                const tree = await getMasterTreeByOrderItemId(orderItemId, b, initialProductModelId);
                if (!cancelled) {
                    setMasterTree(tree);
                    setVariants(
                        variantList.map((item) => ({
                            ...item,
                            selected: Number(item?.product_model_id) === Number(initialProductModelId),
                        }))
                    );
                    setSelectedProductModelId(initialProductModelId);
                }
            } catch (fetchError) {
                if (!cancelled) {
                    setError("No se pudo cargar el diseñador para este order item.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [orderItemId]);

    const handleSelectVariant = async (variantItem) => {
        if (!orderItemId || !variantItem?.product_model_id) return;
        const targetProductModelId = Number(variantItem.product_model_id);
        if (Number(selectedProductModelId) === targetProductModelId) {
            return;
        }

        setSwitchingVariant(true);
        setVariantError("");
        try {
            const reversible = await getIfGarmentIsReversible(orderItemId, targetProductModelId);
            const b = Number(reversible?.is_reversible) === 1 ? 1 : 0;
            const tree = await getMasterTreeByOrderItemId(orderItemId, b, targetProductModelId);
            setBSide(b);
            setMasterTree(tree);
            setSelectedProductModelId(targetProductModelId);
            setVariants((prev) =>
                (prev || []).map((item) => ({
                    ...item,
                    selected: Number(item?.product_model_id) === targetProductModelId,
                }))
            );
        } catch (err) {
            setVariantError("No se pudo cambiar a la variante seleccionada.");
        } finally {
            setSwitchingVariant(false);
        }
    };

    if (loading) {
        return (
            <section className={styles.pageLoaderOnly}>
                <div className={styles.pageLoaderContent}>
                    <img
                        src={designerLoadingGif}
                        alt="Cargando diseñador"
                        className={styles.pageLoaderOnlyImage}
                    />
                    <span className={styles.pageLoaderText}>Cargando...</span>
                </div>
            </section>
        );
    }

    if (error || !masterTree) {
        return (
            <section className={styles.sectionCard}>
                <p className={styles.summaryHint}>{error || "No hay datos para el diseñador."}</p>
                <button
                    type="button"
                    className={styles.btn}
                    onClick={() => navigate("/mcb/prendas")}
                >
                    Volver a prendas
                </button>
            </section>
        );
    }

    return (
        <Designer
            variant="page"
            isOpen={true}
            onClose={() => navigate("/mcb/prendas")}
            masterTree={masterTree}
            orderItemId={orderItemId}
            bSide={bSide}
            productModelId={selectedProductModelId}
            customerId={contextData?.customer?.id || null}
            variants={variants}
            switchingVariant={switchingVariant}
            variantError={variantError}
            onSelectVariant={handleSelectVariant}
        />
    );
};

export default DesignerPage;
